import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBook,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSpinner,
  FaInfoCircle,
  FaPencilAlt,
} from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Homework = {
  homeworkId: number;
  title: string;
  level: string;
  chapterNumber: number;
  instructions: string | null;
  deadline: string; // ISO string
  completed: boolean;
  score: number | null;
  tasks: any[]; // We'll use this to show task count
};

const StudentMyHomeworkPage: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();

  const [homework, setHomework] = useState<Homework[]>([]);
  const [batch, setBatch] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    fetchData();
  }, [student]);

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      // Get student's batch
      const batchResponse = await fetch(
        `${API_BASE_URL}/api/student/my-batch`,
        { credentials: "include" }
      );

      if (batchResponse.ok) {
        const batchData = await batchResponse.json();
        setBatch(batchData);

        const batchId = batchData.id || batchData.batchId;

        if (batchId) {
          // Fetch homework for this batch
          const homeworkResponse = await fetch(
            `${API_BASE_URL}/api/student/homework/my-batch?batchId=${batchId}`,
            { credentials: "include" }
          );

          if (homeworkResponse.ok) {
            const homeworkData = await homeworkResponse.json();
            setHomework(homeworkData);
          } else {
            setError("Failed to load homework");
          }
        } else {
          setError("Batch ID not found");
        }
      } else {
        setError("Failed to load batch information");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load homework");
    } finally {
      setLoading(false);
    }
  };

  const getHomeworkStatus = (hw: Homework) => {
    if (hw.completed) return "completed";
    if (hw.deadline && new Date(hw.deadline) < new Date()) return "missed";
    return "pending";
  };

  const truncateText = (text: string, maxLength: number = 80) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaBook />}
        title="My Homework (Hausaufgabe)"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Homework" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Info Card */}
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FaInfoCircle className="text-blue-600" />
            How It Works
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">1.</span>
              <span>View your assigned homework below</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">2.</span>
              <span>Click "Start" to begin completing homework tasks</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">3.</span>
              <span>
                Answer all tasks in the homework (multiple task types possible)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">4.</span>
              <span>
                Submit before the deadline to get feedback and score out of 20
              </span>
            </li>
          </ul>
        </div>

        {/* Homework List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white p-6">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <FaBook />
              Your Homework
            </h3>
            <p className="text-cyan-100 text-sm mt-1">
              Complete homework and track your progress
            </p>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <FaSpinner className="animate-spin text-[#009fe3] text-4xl mx-auto mb-4" />
                <p className="text-gray-600">Loading your homework...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            ) : !batch ? (
              <div className="text-center py-12 bg-amber-50 border border-amber-200 rounded-xl">
                <FaBook className="mx-auto h-12 w-12 text-amber-400 mb-4" />
                <h4 className="text-lg font-medium text-gray-700 mb-2">
                  No Batch Assigned
                </h4>
                <p className="text-gray-500 text-sm">
                  You need to be assigned to a batch to access homework.
                </p>
              </div>
            ) : homework.length === 0 ? (
              <div className="text-center py-12">
                <FaBook className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                <h4 className="text-lg font-medium text-gray-700 mb-2">
                  No Homework Yet
                </h4>
                <p className="text-gray-500 text-sm">
                  No homework has been assigned to your batch yet.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Chapter
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Title
                      </th>
                      <th className="py-3 px-4 text-center text-sm font-semibold text-gray-700">
                        Tasks
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Deadline
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Score
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {homework.map((hw, index) => {
                      const status = getHomeworkStatus(hw);
                      return (
                        <tr
                          key={hw.homeworkId}
                          className={`border-b border-gray-100 ${
                            index % 2 === 1 ? "bg-gray-50" : "bg-white"
                          } hover:bg-blue-50 transition-colors`}
                        >
                          <td className="py-4 px-4">
                            <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-800 font-bold rounded-md">
                              {hw.chapterNumber}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-gray-700 text-sm font-medium">
                              {hw.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Level: {hw.level}
                            </p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-800">
                              {hw.tasks.length} tasks
                            </span>
                          </td>
                          <td className="py-4 px-4 text-gray-600 text-sm">
                            {hw.deadline ? (
                              <div className="flex items-center gap-1">
                                <FaClock className="text-xs" />
                                {new Date(hw.deadline).toLocaleString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {status === "completed" && (
                              <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full text-xs font-semibold border border-emerald-200">
                                <FaCheckCircle className="text-emerald-500" />
                                Completed
                              </span>
                            )}
                            {status === "pending" && (
                              <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 px-3 py-1 rounded-full text-xs font-semibold border border-amber-200">
                                <FaClock className="text-amber-500" />
                                Pending
                              </span>
                            )}
                            {status === "missed" && (
                              <span className="inline-flex items-center gap-1.5 text-rose-700 bg-rose-50 px-3 py-1 rounded-full text-xs font-semibold border border-rose-200">
                                <FaTimesCircle className="text-rose-500" />
                                Missed
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {hw.score !== null ? (
                              <span className="font-semibold text-gray-900">
                                {hw.score.toFixed(1)}/20
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {status === "completed" ? (
                              <button
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
                                onClick={() =>
                                  navigate(
                                    `/student/homework/${hw.homeworkId}/submission`
                                  )
                                }
                                title="View submission"
                              >
                                <FaCheckCircle />
                                View
                              </button>
                            ) : status === "missed" ? (
                              <button
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm bg-gray-100 text-gray-400 cursor-not-allowed"
                                disabled
                                title="Deadline missed"
                              >
                                <FaTimesCircle />
                                Missed
                              </button>
                            ) : (
                              <button
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm bg-[#009fe3] text-white hover:bg-cyan-600 hover:shadow-md"
                                onClick={() =>
                                  navigate(
                                    `/student/homework/${hw.homeworkId}/complete`
                                  )
                                }
                                title="Start homework"
                              >
                                <FaPencilAlt />
                                Start
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentMyHomeworkPage;
